/**
 * Main navigation component providing both desktop drawer and mobile-optimized navigation.
 * Features bottom navigation for key actions on mobile and hamburger menu for secondary items.
 * Update when adding new navigation routes or modifying mobile navigation patterns.
 */
import React from 'react';
import { Drawer } from '@mui/material';
import { styled } from '@mui/material/styles';

import { accountStateOf } from '@/auth/accountState';
import {
  DRAWER_WIDTH,
  getGroupedHamburgerMenuItems,
  getGroupedNavigationItems,
  getHamburgerMenuItems,
  getNavigationItems,
  NAV_RAIL_WIDTH,
  navWidthTransitionSx,
} from '@/config/navigation';
import { ROLES } from '@/config/roles';
import { useAuth } from '@/hooks/useAuth';
import { useFocusManagement } from '@/utils/focusManagement';

import AuthSection from './components/AuthSection';
import DrawerHeader from './components/DrawerHeader';
import LogoHeader from './components/LogoHeader';
import MobileBottomNavigation from './components/MobileBottomNavigation';
import type { NavItemData } from './components/NavigationItem';
import NavigationList from './components/NavigationList';
import UserInfo from './components/UserInfo';
import { useNavigation } from './hooks/useNavigation';

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    backgroundColor: theme.palette.background.default,
    boxSizing: 'border-box',
    width: DRAWER_WIDTH,
    borderRight: 'none',
    boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: '280px',
    },
  },
}));

interface NavigationDrawerProps {
  mobileOpen?: boolean;
  handleDrawerToggle?: () => void;
  userRole: string;
  /** Desktop drawer collapsed to the icon rail (see useNavRail). */
  railActive?: boolean;
  onToggleRail?: () => void;
}

const NavigationDrawer: React.FC<NavigationDrawerProps> = ({
  mobileOpen = false,
  handleDrawerToggle = () => {},
  userRole,
  railActive = false,
  onToggleRail = () => {},
}) => {
  const { user, isRoleLoaded, userStatus, authError, demoView } = useAuth();
  // No tabs until the server has confirmed the role and the account is
  // active: the provider's starting default is a student, and an account
  // awaiting activation has nowhere to go but /pending-approval. More (with
  // Log in / Log out) stays.
  const navRole =
    accountStateOf({
      isRoleLoaded,
      userStatus,
      authError: authError ?? null,
    }) === 'active'
      ? userRole
      : '';
  const {
    location,
    isAuthenticated,
    getBottomNavValue,
    handleBottomNavChange,
    handleAuthAction,
  } = useNavigation(navRole, handleDrawerToggle);

  // Use focus management to handle aria-hidden accessibility issues
  useFocusManagement(mobileOpen);

  // Grouped nav only for admin/super_admin; other roles keep flat list (fewer pages)
  const useGroupedNav =
    navRole === ROLES.SUPER_ADMIN || navRole === ROLES.ADMIN;
  const desktopGroups = useGroupedNav
    ? getGroupedNavigationItems(navRole, false, user)
    : [];
  const hamburgerGroups = useGroupedNav
    ? getGroupedHamburgerMenuItems(navRole, user)
    : [];
  const allNavigationItems = getNavigationItems(navRole, false, user);
  const hamburgerMenuItems = getHamburgerMenuItems(navRole, user);

  // Handle navigation item clicks
  const handleNavigationItemClick = (_item: NavItemData) => {
    if (handleDrawerToggle) {
      handleDrawerToggle();
    }
  };

  // Handle auth actions with drawer toggle
  const handleAuthWithToggle = (action: 'login' | 'logout') => {
    handleAuthAction(action, handleDrawerToggle);
  };

  // Mobile hamburger menu (grouped for admin/super_admin only)
  const hamburgerMenuContent = (
    <div>
      <LogoHeader />
      <UserInfo />
      <NavigationList
        groups={
          useGroupedNav && hamburgerGroups.length > 0
            ? hamburgerGroups
            : undefined
        }
        items={
          !useGroupedNav || hamburgerGroups.length === 0
            ? hamburgerMenuItems
            : undefined
        }
        currentPath={location.pathname}
        currentSearch={location.search}
        onItemClick={handleNavigationItemClick}
      />
      <AuthSection
        isAuthenticated={isAuthenticated}
        onAuthAction={handleAuthWithToggle}
      />
    </div>
  );

  // Desktop drawer (grouped for admin/super_admin only)
  const desktopDrawerContent = (
    <div>
      <DrawerHeader rail={railActive} onToggleRail={onToggleRail} />
      <NavigationList
        groups={
          useGroupedNav && desktopGroups.length > 0 ? desktopGroups : undefined
        }
        items={
          !useGroupedNav || desktopGroups.length === 0
            ? allNavigationItems
            : undefined
        }
        currentPath={location.pathname}
        currentSearch={location.search}
        rail={railActive}
      />
    </div>
  );

  return (
    <>
      {/* Mobile Hamburger Menu */}
      <StyledDrawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        anchor="right"
        ModalProps={{
          keepMounted: true,
          // Prevent focus from being trapped outside the modal
          disableEnforceFocus: false,
          // Ensure proper focus management
          disableAutoFocus: false,
          disableRestoreFocus: false,
        }}
        sx={{ display: { xs: 'block', sm: 'none' } }}
      >
        {hamburgerMenuContent}
      </StyledDrawer>

      {/* Desktop Drawer */}
      <StyledDrawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            width: railActive ? NAV_RAIL_WIDTH : DRAWER_WIDTH,
            overflowX: 'hidden',
            ...navWidthTransitionSx('width'),
          },
        }}
        open
      >
        {desktopDrawerContent}
      </StyledDrawer>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNavigation
        userRole={navRole}
        user={user}
        value={getBottomNavValue()}
        onChange={handleBottomNavChange}
        demoViewActive={Boolean(demoView?.available && demoView.active)}
      />
    </>
  );
};

export default NavigationDrawer;
